// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ExportingConfiguration.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import Panel from 'share/components/controls/Izenda.Panel';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import action from './Izenda.ExportingConfigurationActions';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import IzendaSwitch from 'share/components/controls/Izenda.Switch';
import SettingActionHeader from '../Izenda.SettingActionHeader';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import NumericInput from 'share/components/controls/Izenda.NumericInput';
import EventUtils from 'EventUtils';
import DropDown from 'share/components/controls/Izenda.DropDown';

import { REQUIRE_CONSTRAINTS, INTERVAL_REQUIRE_CONSTRAINTS, STORAGE_TYPES, max, min } from 'logic/data/Izenda.ExportingConfigurationConstants';
const ValidateableNumeric = Validator()(NumericInput);
const ValidateableInput = Validator()(Input);

import { isNil } from 'lodash/fp';

@connect(action)
export default class ExportingConfiguration extends Component {
  renderExportConfiguration() {
    const {
      localization,
      store,
      store: {
        exportingConfiguration: {
          exportQueueFileStorage,
          isEnableSmartExporting,
          isEnableAsyncExportEncryption,
          asyncExportingInterval
        } 
      } 
    } = this.props;

    const onChangeStorageType = (value) => {
      store.setValueByKey('exportQueueFileStorage', value.value);
    }

    const onEnableSmartExportingChange = (e) => {
      store.setValueByKey('isEnableSmartExporting', e.target.checked);
    }

    const onEnableAsyncExportingEncryptionChange = (e) => {
      store.setValueByKey('isEnableAsyncExportEncryption', e.target.checked);
    }

    const onAsyncExportingIntervalChange = (value) => {
      store.setValueByKey('asyncExportingInterval', value);
    }

    return (
      <Panel className='u_marginTop panel-default Izenda_CachingInfo'>
        <GridLayout>
          <GridLayout.Row>
              <GridLayout.Col cols={2}>
                <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_ASYNC_EXPORTING_INTERVAL')}</label>
              </GridLayout.Col>
              <GridLayout.Col cols={2}>
                <ValidateableNumeric
                  value={asyncExportingInterval}
                  onChange={onAsyncExportingIntervalChange}
                  validations={INTERVAL_REQUIRE_CONSTRAINTS}
                  field={'asyncExportingInterval'}
                  max={max}
                  min={min}
                  isDecimal={false}
                  isPositive
                />
              </GridLayout.Col>
              <GridLayout.Col cols={0.5}>
                <label>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_TIME_UNIT')}</label>
              </GridLayout.Col>
          </GridLayout.Row>
          <GridLayout.Row>
              <GridLayout.Col cols={2}>
                <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_EXPORT_STORAGE_OPTION')}</label>
              </GridLayout.Col>
              <GridLayout.Col cols={2}>
                <DropDown
                  options={STORAGE_TYPES}
                  onChange={onChangeStorageType}
                  value={exportQueueFileStorage}
                  labelField='title'
                  valueField='value' />
              </GridLayout.Col>
          </GridLayout.Row>
          {this.renderFileStorageConfiguration()}
          <GridLayout.Row>
            <GridLayout.Col cols={2}>
              <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_SMART_EXPORTING')}</label>
            </GridLayout.Col>
            <GridLayout.Col cols={10}>
              <IzendaSwitch id="izenda-exporting-smart" value={isEnableSmartExporting} onChange={onEnableSmartExportingChange} />
            </GridLayout.Col>
          </GridLayout.Row>
          <GridLayout.Row>
            <GridLayout.Col cols={2}>
              <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_ENCRYPTION')}</label>
            </GridLayout.Col>
            <GridLayout.Col cols={10}>
              <IzendaSwitch id="izenda-exporting-encryption" value={isEnableAsyncExportEncryption} onChange={onEnableAsyncExportingEncryptionChange} />
            </GridLayout.Col>
          </GridLayout.Row>
        </GridLayout>
      </Panel>
    );
  }

  renderFileStorageConfiguration(){
    const {
      localization,
      store,
      store: {
        exportingConfiguration: {
          exportQueueFileStorage,
          asyncExportingPath,
          azureFileSrvceCnnctionString,
          awsS3BucketName,
          awsAccessKey,
          awsSecretKey,
          region
        } 
      } 
    } = this.props;

    const onAsyncExportingPathChange = (value) => {
      store.setValueByKey('asyncExportingPath', value);
    }

    const onAzureFileServiceConnectionStringChange = (value) => {
      store.setValueByKey('azureFileSrvceCnnctionString', value);
    }

    const onAWSS3BucketNameChange = (value) => {
      store.setValueByKey('awsS3BucketName', value);
    }

    const onAWSAccessKeyChange = (value) => {
      store.setValueByKey('awsAccessKey', value);
    }

    const onAWSSecretKeyChange = (value) => {
      store.setValueByKey('awsSecretKey', value);
    }

    const onRegionChange = (value) => {
      store.setValueByKey('region', value);
    }

    if(exportQueueFileStorage === STORAGE_TYPES[0].value){
      return (
        <GridLayout.Row>
          <GridLayout.Col cols={2}>
            <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_FILE_REPOSITORY')}</label>
          </GridLayout.Col>
          <GridLayout.Col cols={10}>
            <ValidateableInput field={'asyncExportingPath'} type='text' className='form-control' validations={REQUIRE_CONSTRAINTS} value={asyncExportingPath} onChange={EventUtils.notifyChange(onAsyncExportingPathChange, EventUtils.INPUT)} />
          </GridLayout.Col>
        </GridLayout.Row>
      );
    }

    if(exportQueueFileStorage === STORAGE_TYPES[1].value){
      return (
        <GridLayout.Row>
          <GridLayout.Col cols={2}>
            <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_AZURE_FILES_CONNECTION_STRING')}</label>
          </GridLayout.Col>
          <GridLayout.Col cols={10}>
            <ValidateableInput field={'azureFileSrvceCnnctionString'} type='text' className='form-control' validations={REQUIRE_CONSTRAINTS} value={azureFileSrvceCnnctionString} onChange={EventUtils.notifyChange(onAzureFileServiceConnectionStringChange, EventUtils.INPUT)} />
          </GridLayout.Col>
        </GridLayout.Row>
      );
    }

    if(exportQueueFileStorage === STORAGE_TYPES[2].value){
      return (
        <GridLayout.Row>
          <GridLayout.Col>
            <GridLayout>
              <GridLayout.Row>
                <GridLayout.Col cols={2}>
                  <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_AWS_BUCKET_NAME')}</label>
                </GridLayout.Col>
                <GridLayout.Col cols={2}>
                  <ValidateableInput field={'awsS3BucketName'} type='text' className='form-control' validations={REQUIRE_CONSTRAINTS} value={awsS3BucketName} onChange={EventUtils.notifyChange(onAWSS3BucketNameChange, EventUtils.INPUT)} />
                </GridLayout.Col>
                <GridLayout.Col cols={1}></GridLayout.Col>
                <GridLayout.Col cols={2}>
                  <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_AWS_ACCESS_KEY')}</label>
                </GridLayout.Col>
                <GridLayout.Col cols={2}>
                  <ValidateableInput field={'awsAccessKey'} type='text' className='form-control' validations={REQUIRE_CONSTRAINTS} value={awsAccessKey} onChange={EventUtils.notifyChange(onAWSAccessKeyChange, EventUtils.INPUT)} />
                </GridLayout.Col>
              </GridLayout.Row>
              <GridLayout.Row>
                <GridLayout.Col cols={2}>
                  <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_AWS_SECRET_KEY')}</label>
                </GridLayout.Col>
                <GridLayout.Col cols={2}>
                  <ValidateableInput field={'awsSecretKey'} type='text' className='form-control' validations={REQUIRE_CONSTRAINTS} value={awsSecretKey} onChange={EventUtils.notifyChange(onAWSSecretKeyChange, EventUtils.INPUT)} />
                </GridLayout.Col>
                <GridLayout.Col cols={1}></GridLayout.Col>
                <GridLayout.Col cols={2}>
                  <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_S3_BUCKET_REGION')}</label>
                </GridLayout.Col>
                <GridLayout.Col cols={2}>
                  <ValidateableInput field={'region'} type='text' className='form-control' validations={REQUIRE_CONSTRAINTS} value={region} onChange={EventUtils.notifyChange(onRegionChange, EventUtils.INPUT)} />
                </GridLayout.Col>
              </GridLayout.Row>
            </GridLayout>
          </GridLayout.Col>
        </GridLayout.Row>
      );
    }
  }

  renderRoutineFileProcess() {
    const {
      localization,
      store,
      store: {
        exportingConfiguration: { isRefreshExportingInterval, asyncRefreshExportingInterval }
      } 
    } = this.props;

    const onRefreshExportingIntervalChange = (value) => {
      if(!value){
        store.setValueByKey('asyncRefreshExportingInterval', 0);
      }
      store.setValueByKey('isRefreshExportingInterval', value);
    }

    const onAsyncRefreshExportingIntervalChange = (value) => {
      store.setValueByKey('asyncRefreshExportingInterval', value);
    }

    return (
      <Panel className='u_marginTop panel-default Izenda_CachingInfo' header={localization.getText('SYSTEM_CONFIGURATION_EXPORTING_TITLE_ROUTINE_FILE_PROCESS')}>
        <GridLayout>
            <GridLayout.Row>
              <GridLayout.Col cols={2}>
                <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_FILE_REFRESH')}</label>
              </GridLayout.Col>
              <GridLayout.Col cols={10}>
                <Input checked={isRefreshExportingInterval} type='checkbox' onChange={EventUtils.notifyChange(onRefreshExportingIntervalChange, EventUtils.CHECKBOX)} />
              </GridLayout.Col>
            </GridLayout.Row>
            {isRefreshExportingInterval && (
              <GridLayout.Row>
                <GridLayout.Col cols={2}>
                  <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_FILE_REFRESH_INTERVAL')}</label>
                </GridLayout.Col>
                <GridLayout.Col cols={2}>
                  <ValidateableNumeric
                    value={asyncRefreshExportingInterval}
                    onChange={onAsyncRefreshExportingIntervalChange}
                    validations={INTERVAL_REQUIRE_CONSTRAINTS}
                    field={'asyncRefreshExportingInterval'}
                    max={max}
                    min={min}
                    isDecimal={false}
                    isPositive
                  />
                </GridLayout.Col>
                <GridLayout.Col cols={0.5}>
                  <label>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_TIME_UNIT')}</label>
                </GridLayout.Col>
              </GridLayout.Row>
            )}
            </GridLayout>
      </Panel>
    );
  }

  componentWillUnmount() {
    this.props.store.destroy();
  }

  componentDidMount() {
    const { store, loader } = this.props;
    store.init();
    loader.startLoading();
    store.loadExportingConfiguration().finally(loader.endLoading.bind(loader));
  }

  render() {
    const {
      localization,
      store,
      store: {
        exportingConfiguration: { isEnableAsyncExporting }
      } 
    } = this.props;

    const onEnableAsyncExportingChange = (e) => {
      store.setValueByKey('isEnableAsyncExporting', e.target.checked);
    }

    return (
      <div className='izenda-GoogleMapConfig'>
        <ScrollBox fullWidth>
          <SettingActionHeader {...this.props} />
          <Panel className='u_marginTop panel-default Izenda_CachingInfo' header={localization.getText('SYSTEM_CONFIGURATION_EXPORTING_TITLE_ASYNC_EXPORT_SERVICE')}>
            <GridLayout>
              <GridLayout.Row>
                <GridLayout.Col cols={2}>
                  <label className='u_marginLeft'>{localization.getText('SYSTEM_CONFIGURATION_EXPORTING_LABEL_EXPORT_SERVICE')}</label>
                </GridLayout.Col>
                <GridLayout.Col cols={10}>
                  <IzendaSwitch id="izenda-export-service" value={isEnableAsyncExporting} onChange={onEnableAsyncExportingChange} />
                </GridLayout.Col>
              </GridLayout.Row>
            </GridLayout>
          </Panel>
          {isEnableAsyncExporting && this.renderExportConfiguration()}
          {isEnableAsyncExporting && this.renderRoutineFileProcess()}
        </ScrollBox>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/SystemConfiguration/Exporting/Izenda.ExportingConfiguration.jsx